import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  Typography,
} from "@mui/material";
import InputText from "../InputFields/InputText";
import "./style.css";
import AxiosInstance from "../../utils/axiosInstance";
import TruncatedText from "../Tooltip/TruncatedText";
import { getItem } from "../../utils/storage";
import {
  accumulateLineItemSubtotals,
  computeTaxDetailsFromDefinitions,
} from "../../utils/lineItemTaxBreakdown";
import { formatUSD } from "../../utils/usFormat";

const DiscountTable = ({
  subTotal,
  discountAmount,
  taxAmount,
  Total,
  formik,
  lineItems = [],
  onTaxesCalculated,
  tokenDecode,
  isNySurcharge = false,
  cardFeeConfig = null,
}) => {
  const onTaxesCalculatedRef = useRef(onTaxesCalculated);
  onTaxesCalculatedRef.current = onTaxesCalculated;

  const [showDiscount, setShowDiscount] = useState(false);
  const [showTax, setShowTax] = useState(false);
  const [taxes, setTaxes] = useState([]);
  const CompanyId = React.useMemo(() => {
    return getItem("CompanyId") || tokenDecode?.CompanyId || tokenDecode?.companyId;
  }, [tokenDecode]);

  // Calculate subtotals and check types in a single pass
  // Exclude non-taxable items from tax calculations
  const { hasMaterials, hasLabor, materialSubTotal, laborSubTotal, taxableMaterialSubTotal, taxableLaborSubTotal } =
    React.useMemo(() => accumulateLineItemSubtotals(lineItems), [lineItems]);

  const {
    totalMaterialTaxes,
    totalLaborTaxes,
    materialTaxDetails,
    laborTaxDetails,
  } = React.useMemo(
    () =>
      computeTaxDetailsFromDefinitions(taxes, {
        hasMaterials,
        hasLabor,
        taxableMaterialSubTotal,
        taxableLaborSubTotal,
      }),
    [taxes, hasMaterials, hasLabor, taxableMaterialSubTotal, taxableLaborSubTotal]
  );

  const totalTaxes = totalMaterialTaxes + totalLaborTaxes;

  const allTaxDetails = React.useMemo(
    () => [...materialTaxDetails, ...laborTaxDetails],
    [materialTaxDetails, laborTaxDetails]
  );

  // Helper function to normalize tax type display (show "Materials" instead of "Material")
  const normalizeTaxTypeDisplay = (type) => {
    if (!type) return "";
    // Convert "Material" to "Materials" for display (backward compatibility)
    if (type === "Material" || type.toLowerCase() === "material") {
      return "Materials";
    }
    // Already "Materials", return as is
    return type;
  };

  // Calculate final total including all taxes (API taxes + custom tax).
  // When NY surcharge is active, the merchant discount is suppressed. Line-item
  // prices are already inflated upstream, so Subtotal already reflects the
  // surcharge — Total is just Subtotal + Tax.
  const effectiveDiscount = isNySurcharge ? 0 : (discountAmount || 0);
  const finalTotal =
    (subTotal || 0) + totalTaxes + (taxAmount || 0) - effectiveDiscount;
  useEffect(() => {
    const fetchTaxes = async () => {
      try {
        if (!CompanyId) {
          console.warn("CompanyId not available for tax fetch");
          return;
        }

        const response = await AxiosInstance.get(`/v1/taxes`, {
          params: {
            CompanyId: CompanyId,
          },
        });

        if (response?.data?.data) {
          setTaxes(response.data.data);
        } else {
          console.warn("No tax data received from API");
          setTaxes([]);
        }
      } catch (error) {
        console.error("Error fetching taxes:", error?.response?.data || error);
        setTaxes([]);
      }
    };
    fetchTaxes();
  }, [CompanyId]);

  // Sync tax totals to parent when derived values change. Callback is kept in a ref so an
  // inline parent handler does not retrigger this effect every render (update loop).
  useEffect(() => {
    const cb = onTaxesCalculatedRef.current;
    if (cb) {
      cb({
        totalTaxes,
        taxDetails: allTaxDetails,
        materialSubTotal,
        laborSubTotal,
      });
    }
  }, [totalTaxes, allTaxDetails, materialSubTotal, laborSubTotal]);
  return (
    <Table sx={{ width: '100%', tableLayout: 'auto', maxWidth: '100%' }}>
      <TableBody>
        {hasMaterials && (
          <>
            <TableRow>
              <TableCell
                className="text-blue-color fw-medium"
                style={{ height: "50px", fontSize: "16px" }}
              >
                Materials Subtotal
              </TableCell>
              <TableCell
                className="text-end text-blue-color fw-medium"
                style={{ fontSize: "16px" }}
              >
                {`$${
                  new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(materialSubTotal) || ""
                }`}
              </TableCell>
            </TableRow>

            {taxes.length > 0 && taxableMaterialSubTotal > 0 &&
              taxes
                .filter((tax) => tax.types && (tax.types.includes("Materials") || tax.types.includes("Material")))
                .map((tax) => {
                  const feeType = tax.feeType || "Percentage";
                  // When feeType is "Percentage", use percentage field; when "Fixed", use feeAmount field
                  const value = feeType === "Percentage" ? (tax.percentage || 0) : (tax.feeAmount || 0);
                  let taxValue = 0;
                  // Use taxableMaterialSubTotal to exclude non-taxable items
                  if (feeType === "Percentage") {
                    taxValue = (taxableMaterialSubTotal * value) / 100;
                  } else {
                    // For Fixed fee type, only apply if there's taxable material
                    taxValue = taxableMaterialSubTotal > 0 ? value : 0;
                  }
                  
                  
                  
                  // Only show tax row if taxValue > 0 (meaning there's taxable material)
                  if (taxValue <= 0) {
                    
                    return null;
                  }
                  
                  const taxTypesDisplay = Array.isArray(tax.types) 
                    ? tax.types.map(normalizeTaxTypeDisplay).join(", ")
                    : normalizeTaxTypeDisplay(tax.type || "");
                  const taxDisplay = feeType === "Percentage" 
                    ? `${tax.name || ""} (${value || 0}%) - ${taxTypesDisplay}`
                    : `${tax.name || ""} ($${value || 0}) - ${taxTypesDisplay}`;
                  return (
                    <TableRow key={`material-${tax.id}`}>
                      <TableCell
                        className="text-blue-color fw-medium"
                        style={{
                          height: "50px",
                          fontSize: "16px",
                          paddingLeft: "20px",
                        }}
                      >
                        <TruncatedText
                          text={taxDisplay}
                          maxLength={25}
                          style={{ maxWidth: "250px",textTransform: "capitalize" }}
                          tooltipPlacement="top"
                        />
                        
                      </TableCell>
                      <TableCell
                        className="text-end text-blue-color fw-medium"
                        style={{ fontSize: "16px" }}
                      >
                        {formatUSD(taxValue)}
                      </TableCell>
                    </TableRow>
                  );
                })
                .filter(Boolean) // Remove null entries (taxes with 0 value)
            }
          </>
        )}

        {/* Labor Section */}
        {hasLabor && (
          <>
            <TableRow>
              <TableCell
                className="text-blue-color fw-medium"
                style={{ height: "50px", fontSize: "16px" }}
              >
                Labor Subtotal
              </TableCell>
              <TableCell
                className="text-end text-blue-color fw-medium"
                style={{ fontSize: "16px" }}
              >
                {`$${
                  new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(laborSubTotal) || ""
                }`}
              </TableCell>
            </TableRow>

            {taxes.length > 0 && taxableLaborSubTotal > 0 &&
              taxes
                .filter((tax) => tax.types && tax.types.includes("Labor"))
                .map((tax) => {
                  const feeType = tax.feeType || "Percentage";
                  // When feeType is "Percentage", use percentage field; when "Fixed", use feeAmount field
                  const value = feeType === "Percentage" ? (tax.percentage || 0) : (tax.feeAmount || 0);
                  let taxValue = 0;
                  // Use taxableLaborSubTotal to exclude non-taxable items
                  if (feeType === "Percentage") {
                    taxValue = (taxableLaborSubTotal * value) / 100;
                  } else {
                    // For Fixed fee type, only apply if there's taxable labor
                    taxValue = taxableLaborSubTotal > 0 ? value : 0;
                  }
                  
                    
                  
                  // Only show tax row if taxValue > 0 (meaning there's taxable labor)
                  if (taxValue <= 0) {
                    
                    return null;
                  }
                  
                  const taxTypesDisplay = Array.isArray(tax.types) 
                    ? tax.types.map(normalizeTaxTypeDisplay).join(", ")
                    : normalizeTaxTypeDisplay(tax.type || "");
                  const taxDisplay = feeType === "Percentage" 
                    ? `${tax.name || ""} (${value || 0}%) - ${taxTypesDisplay}`
                    : `${tax.name || ""} ($${value || 0}) - ${taxTypesDisplay}`;
                  return (
                    <TableRow key={`labor-${tax.id}`}>
                      <TableCell
                        className="text-blue-color fw-medium"
                        
                        style={{
                          height: "50px",
                          fontSize: "16px",
                          paddingLeft: "20px",
                        }}
                      >
                        <TruncatedText
                          text={taxDisplay}
                          maxLength={25}
                          style={{ maxWidth: "250px",textTransform: "capitalize" }}
                          tooltipPlacement="top"
                        />
                      </TableCell>
                      <TableCell
                        className="text-end text-blue-color fw-medium"
                        style={{ fontSize: "16px" }}
                      >
                        {formatUSD(taxValue)}
                      </TableCell>
                    </TableRow>
                  );
                })
                .filter(Boolean) // Remove null entries (taxes with 0 value)
            }
          </>
        )}

        {!isNySurcharge && (
        <TableRow>
          <TableCell
            className="text-blue-color fw-medium"
            style={{ height: "50px", fontSize: "16px" }}
          >
            Discount
          </TableCell>
          <TableCell
            className={`d-flex discountTableBox ${
              !showDiscount
                ? "justify-content-end"
                : "justify-content-between" || ""
            }`}
            style={{ fontSize: "16px" }}
          >
            {showDiscount && (
              <Typography
                className="discountInputBox"
                style={{ position: "relative", display: "inline-block" }}
              >
                <InputText
                  id="Discount"
                  name="Discount"
                  placeholder="0.00"
                  className="text-blue-color"
                  style={{
                    fontSize: "14px",
                    paddingLeft: "15px",
                  }}
                  type="text"
                  value={formik?.values?.Discount}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (/^\d{0,3}(\.\d{0,2})?$/.test(value)) {
                      if (value === "" || parseFloat(value) <= 100) {
                        formik.setFieldValue("Discount", value);
                      } else {
                        formik.setFieldValue("Discount", "100");
                      }
                    }
                  }}
                  onBlur={formik?.handleBlur}
                />

                <Typography
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                  }}
                  className="text-blue-color"
                >
                  %
                </Typography>
              </Typography>
            )}
            <Typography
              className="text-blue-color underline-u"
              style={{
                fontWeight: 600,
                cursor: "pointer",
                textAlign: "end",
                marginBottom: "9px",
                fontSize: "16px",
              }}
              onClick={() => setShowDiscount(!showDiscount)}
            >
              {discountAmount > 0
                ? formatUSD(discountAmount)
                : "Add Discount"}
            </Typography>
          </TableCell>
        </TableRow>
        )}
        {taxes.length > 0 && (
        <TableRow>
          <TableCell
            className="text-blue-color fw-medium"
            style={{ height: "50px", fontSize: "16px" }}
          >
            Tax
          </TableCell>
          <TableCell
            className={`d-flex discountTableBox   ${
              !showTax ? "justify-content-end" : "justify-content-between"
            }`}
            style={{ fontSize: "16px" }}
          >
            {showTax && (
              <Typography
                style={{ position: "relative", display: "inline-block" }}
                className="discountInputBox"
              >
                <InputText
                  id="Tax"
                  name="Tax"
                  type="text"
                  placeholder="0.00"
                  className="text-blue-color"
                  style={{ fontSize: "16px", paddingLeft: "15px" }}
                  value={formik?.values?.Tax}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (/^\d{0,3}(\.\d{0,2})?$/.test(value)) {
                      if (value === "" || parseFloat(value) <= 100) {
                        formik.setFieldValue("Tax", value);
                      } else {
                        formik.setFieldValue("Tax", "100");
                      }
                    }
                  }}
                  onBlur={formik?.handleBlur}
                />

                <Typography
                  className="text-blue-color"
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                  }}
                >
                  %
                </Typography>
              </Typography>
            )}
            <Typography
              className="text-blue-color underline-u discountInputBox"
              style={{
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: "9px",
                fontSize: "16px",
                textAlign: "end",
              }}
              onClick={() => setShowTax(!showTax)}
            >
              {taxAmount > 0 ? formatUSD(taxAmount) : "Add Tax"}
            </Typography>
          </TableCell>
        </TableRow>
        )}
        <TableRow className="last-row border-0">
          <TableCell
            className="border-0 text-blue-color"
            style={{ fontSize: "16px" }}
          >
            <b>Total</b>
          </TableCell>
          <TableCell
            className="text-end border-0 text-blue-color"
            style={{ fontSize: "16px" }}
          >
            <b>{`$${new Intl.NumberFormat("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(finalTotal)}`}</b>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

// Prevent unnecessary re-renders
const MemoizedDiscountTable = React.memo(
  DiscountTable,
  (prevProps, nextProps) => {
    // Only re-render if these props change
    return (
      prevProps.subTotal === nextProps.subTotal &&
      prevProps.discountAmount === nextProps.discountAmount &&
      prevProps.taxAmount === nextProps.taxAmount &&
      prevProps.Total === nextProps.Total &&
      prevProps.formik?.values?.Discount === nextProps.formik?.values?.Discount &&
      prevProps.formik?.values?.Tax === nextProps.formik?.values?.Tax &&
      prevProps.tokenDecode?.CompanyId === nextProps.tokenDecode?.CompanyId &&
      prevProps.tokenDecode?.companyId === nextProps.tokenDecode?.companyId &&
      prevProps.isNySurcharge === nextProps.isNySurcharge &&
      prevProps.cardFeeConfig?.PricingStructure ===
        nextProps.cardFeeConfig?.PricingStructure &&
      prevProps.cardFeeConfig?.FeeAmount ===
        nextProps.cardFeeConfig?.FeeAmount &&
      prevProps.cardFeeConfig?.FeeType === nextProps.cardFeeConfig?.FeeType &&
      prevProps.cardFeeConfig?.FeeLabel === nextProps.cardFeeConfig?.FeeLabel &&
      JSON.stringify(prevProps.lineItems) ===
        JSON.stringify(nextProps.lineItems)
    );
  }
);

export default MemoizedDiscountTable;
